import DuoFeedbackSheet, {
  praiseKeyFor,
} from '@/components/v2/lesson/DuoFeedbackSheet'
import DuoLessonHeader from '@/components/v2/lesson/DuoLessonHeader'
import DuoOutOfHeartsSheet from '@/components/v2/lesson/DuoOutOfHeartsSheet'
import ImageChoiceExercise from '@/components/v2/lesson/ImageChoiceExercise'
import {
  getLessonContent,
  getLessonPlan,
  LESSON_PHRASE_IDS,
  LESSON_STARTING_HEARTS,
  LESSON_WORD_IDS,
  type LessonExercise,
  type LessonPhraseId,
  type LessonWordId,
} from '@/components/v2/lesson/lessonVocabulary'
import MeaningExercise from '@/components/v2/lesson/MeaningExercise'
import PairsExercise from '@/components/v2/lesson/PairsExercise'
import WordBankExercise from '@/components/v2/lesson/WordBankExercise'
import {
  getSelectedLanguageCode,
  getSelectedLevel,
} from '@/utils/commitmentStorage'
import { useCallback, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Form, useSubmit } from 'react-router-dom'

type LessonStatus = 'answering' | 'correct' | 'out-of-hearts' | 'wrong'

/**
 * The mini-lesson: Duolingo's first lesson, compressed to four exercises. It
 * runs as a single funnel step because the hearts, the streak and the lesson
 * progress bar have to survive between exercises, and because Duolingo treats a
 * lesson as one unbroken unit with its own chrome.
 *
 * The exercises are chosen by the level the learner gave three screens earlier,
 * so a beginner matches pictures to words and someone who said they can hold a
 * conversation builds sentences from a word bank instead.
 *
 * It is not a question, so it posts an empty form: no analytics fields, but it
 * still advances through the route order like every other step.
 */
export default function LessonStep() {
  const { t } = useTranslation()

  // Read once. Re-reading on every render would let a change in another tab
  // swap the lesson out from under a half-finished answer.
  const [plan] = useState(() => getLessonPlan(getSelectedLevel()))
  const [content] = useState(() => getLessonContent(getSelectedLanguageCode()))

  /**
   * Exercises still to do. A missed exercise is appended, which is Duolingo's
   * behaviour and the reason the bar can gain a step mid-lesson.
   */
  const [queue, setQueue] = useState<LessonExercise[]>(plan)
  const [completed, setCompleted] = useState(0)
  const [hearts, setHearts] = useState(LESSON_STARTING_HEARTS)
  const [streak, setStreak] = useState(0)
  const [answered, setAnswered] = useState(0)
  const [status, setStatus] = useState<LessonStatus>('answering')

  const [selected, setSelected] = useState<string | undefined>(undefined)
  const [built, setBuilt] = useState<string[]>([])

  /** Exercises already re-queued once, so a miss cannot loop forever. */
  const retried = useRef(new Set<LessonExercise>())

  const formRef = useRef<HTMLFormElement>(null)
  const submit = useSubmit()

  const exercise = queue[0]

  const prompts = useMemo(
    () => ({
      ...(Object.fromEntries(
        LESSON_WORD_IDS.map((id) => [
          id,
          t(`pages.onboarding.v2.lesson.words.${id}`),
        ]),
      ) as Record<LessonWordId, string>),
      ...(Object.fromEntries(
        LESSON_PHRASE_IDS.map((id) => [
          id,
          t(`pages.onboarding.v2.lesson.phrases.${id}`),
        ]),
      ) as Record<LessonPhraseId, string>),
    }),
    [t],
  )

  /**
   * The bank is the sentence plus decoys, ordered back-to-front so the answer
   * cannot be built by tapping the tiles left to right.
   */
  const bank = useMemo(
    () =>
      [...content.sentence.tokens].reverse().concat(content.sentence.decoys),
    [content],
  )

  const leave = useCallback(() => {
    if (formRef.current) submit(formRef.current)
  }, [submit])

  const expected = (() => {
    if (!exercise) return ''

    switch (exercise.type) {
      case 'image-choice':
      case 'meaning':
        return content.words[exercise.prompt]
      case 'phrase-meaning':
        return content.phrases[exercise.prompt]
      case 'word-bank':
        return content.sentence.tokens.join(' ')
      case 'pairs':
      case 'phrase-pairs':
        return ''
      default: {
        const exhaustiveCheck: never = exercise
        return exhaustiveCheck
      }
    }
  })()

  const settle = (isCorrect: boolean) => {
    setAnswered((previous) => previous + 1)

    if (isCorrect) {
      setStreak((previous) => previous + 1)
      setStatus('correct')
      return
    }

    setStreak(0)

    const remaining = hearts - 1
    setHearts(remaining)

    // Re-ask it later, but only the first time: a learner who cannot get it
    // right is now stuck in front of the account wall rather than past it.
    if (!retried.current.has(exercise)) {
      retried.current.add(exercise)
      setQueue((previous) => [...previous, exercise])
    }

    setStatus(remaining <= 0 ? 'out-of-hearts' : 'wrong')
  }

  const handleCheck = () => {
    if (exercise.type === 'pairs' || exercise.type === 'phrase-pairs') return

    if (exercise.type === 'word-bank') {
      settle(built.join(' ') === expected)
      return
    }

    if (!selected) return

    settle(selected === exercise.prompt)
  }

  // The pairs grid clears itself, so reaching the end is the answer.
  const handleSolved = useCallback(() => {
    setAnswered((previous) => previous + 1)
    setStreak((previous) => previous + 1)
    setStatus('correct')
  }, [])

  const handleContinue = () => {
    if (status === 'correct') setCompleted((previous) => previous + 1)

    const rest = queue.slice(1)

    if (rest.length === 0) {
      leave()
      return
    }

    setQueue(rest)
    setSelected(undefined)
    setBuilt([])
    setStatus('answering')
  }

  const renderExercise = () => {
    const locked = status !== 'answering'

    switch (exercise.type) {
      case 'image-choice':
        return (
          <ImageChoiceExercise
            correctId={exercise.prompt}
            locked={locked}
            onSelect={setSelected}
            options={exercise.options}
            prompt={prompts[exercise.prompt]}
            selected={selected as LessonWordId | undefined}
            vocabulary={content.words}
          />
        )
      case 'pairs':
        return (
          <PairsExercise
            ids={exercise.words}
            onSolved={handleSolved}
            prompts={prompts}
            vocabulary={content.words}
          />
        )
      case 'phrase-pairs':
        return (
          <PairsExercise
            ids={exercise.phrases}
            onSolved={handleSolved}
            prompts={prompts}
            vocabulary={content.phrases}
          />
        )
      case 'meaning':
        return (
          <MeaningExercise
            correctId={exercise.prompt}
            locked={locked}
            onSelect={setSelected}
            options={exercise.options}
            prompt={prompts[exercise.prompt]}
            selected={selected as LessonWordId | undefined}
            titleKey="pages.onboarding.v2.lesson.meaningTitle"
            vocabulary={content.words}
          />
        )
      case 'phrase-meaning':
        return (
          <MeaningExercise
            correctId={exercise.prompt}
            locked={locked}
            onSelect={setSelected}
            options={exercise.options}
            prompt={prompts[exercise.prompt]}
            selected={selected as LessonPhraseId | undefined}
            titleKey="pages.onboarding.v2.lesson.phraseMeaningTitle"
            vocabulary={content.phrases}
          />
        )
      case 'word-bank':
        return (
          <WordBankExercise
            bank={bank}
            locked={locked}
            onChange={setBuilt}
            prompt={t('pages.onboarding.v2.lesson.sentencePrompt')}
          />
        )
      default: {
        const exhaustiveCheck: never = exercise
        return exhaustiveCheck
      }
    }
  }

  const isPairs = exercise.type === 'pairs' || exercise.type === 'phrase-pairs'

  const canCheck =
    exercise.type === 'word-bank' ? built.length > 0 : Boolean(selected)

  return (
    <div className="min-h-dvh bg-white" data-testid="v2-mini-lesson">
      <main className="duo-container flex min-h-dvh flex-col gap-6 pt-6 pb-8">
        <DuoLessonHeader
          completed={completed + (status === 'correct' ? 1 : 0)}
          hearts={hearts}
          onQuit={leave}
          // Grows when a missed exercise is re-queued, so the bar never shows
          // more progress than there is lesson left.
          total={completed + queue.length}
        />

        <div className="flex grow flex-col">{renderExercise()}</div>

        <div className="sticky bottom-0 shrink-0 bg-white">
          {status === 'answering' && (
            <div className="border-duo-line border-t pt-4 pb-2">
              <button
                className="duo-button-primary"
                data-testid="button-check"
                disabled={isPairs || !canCheck}
                onClick={handleCheck}
                type="button"
              >
                {t('pages.onboarding.v2.lesson.check')}
              </button>
            </div>
          )}

          {(status === 'correct' || status === 'wrong') && (
            <DuoFeedbackSheet
              correct={status === 'correct'}
              onContinue={handleContinue}
              praiseKey={praiseKeyFor(answered)}
              solution={expected}
              streak={streak}
            />
          )}

          {status === 'out-of-hearts' && (
            <DuoOutOfHeartsSheet onContinue={leave} />
          )}
        </div>

        {/* Not a question: an empty post still moves the route order along. */}
        <Form method="post" ref={formRef} />
      </main>
    </div>
  )
}
